<script lang="ts" setup>

//#region I M P O R T S
import { computed, getCurrentInstance, nextTick, onMounted, ref, watch, watchSyncEffect } from "vue";
import { DxAutocomplete } from "devextreme-vue/autocomplete";
import { icons } from "@/helpers";
import Card from "./CardMenu.vue";
import Panel from "@/components/panel/PanelWrapper";
import DrawerMobile from "@/components/drawer/DrawerLayout.vue";
import Traducao from '@/pages/dev/painel/traducao/Traducao.js'
import { IndexedDB } from '@/funcoes/indexedDB'
import type { CardProps } from "./types";
import IconProfiles from "@/assets/icons-svg/icon-profiles.svg";
import IconContainer from "@/assets/icons-svg/icon-container.svg";
import CardLoading from './CardLoading.vue'
import {DxButton} from 'devextreme-vue'
import { Drawer, type DrawerProps } from "@/components";
import PainelModules from "@/providers/painel/PainelModules";
import BoxModules from "@/providers/boxes/BoxModules";
//#endregion

//#region I N I C I A L I Z A Ç Ã O

type Props = {
    show: boolean;
    usuario: object;
};

type EmitHandles = {
    onClose: () => void;
};

defineProps<Props>();
defineEmits<EmitHandles>();

const app = getCurrentInstance()
const { $http, $vistra, $store } = app!.appContext.config.globalProperties

const _painelModules = new PainelModules(app);
const _boxModules = new BoxModules(_painelModules);
const traducao = new Traducao(app, _painelModules, _boxModules)

const card = ref<CardProps | null>(null);
const drawer = ref<DrawerProps>();
const panelsContainer = ref();
const categoriesRef = ref();
const search = ref<string | null>(null);
const selected = ref(0);
const carregandoPaineis = ref(true);
const dadosPaineis = ref([]);
const dadosGrupos = ref([]);
const filtro = ref('');
const navegandoSideBar = ref(false);
const drawerMobile = ref<DrawerProps>()
const margemTop = 104;
let optionsDropdownSearch = {
    width: $vistra.getDispositivo() != 'mobile' ? '500px' : '310',
    resizeEnabled: true
}
const idEmpresa = computed(() => $store.state.idEmpresa)

//#endregion

//#region  C A C H E 
// cache dos painéis
const dbPaineis = ref(new IndexedDB({ 
    vistra: $vistra,
    nomeBanco: "vistraPaineis", 
    versao: 2,
    tabelaPadrao: "painel", 
    tabelas: [{ 
        nome: "painel", 
        chave: "idUnico",
        //se alterar os campos tem que incrementar a versao
        campos: [
            { nome: "idUnico", unico: true },
            { nome: "id" },
            { nome: "nome" },
            { nome: "descricao" },
            { nome: "idGrupo" },
            { nome: "nomeGrupo" },
            { nome: "criador" },
            { nome: "dataCriacao" },
            { nome: "dataUltimaAlteracao" },
            { nome: "usuarioUltimaAlteracao" },
            { nome: "ultimoAcesso" },
            { nome: "dataUltimaImagem" },
            { nome: "imagem" },
            { nome: "imagemEscura" },
            { nome: "temImagem" },
            { nome: "ultimaVersao" },
            { nome: "agendamento" },
            { nome: "vistra4" },
            { nome: "traducao" },
            { nome: "permissoesPro" }
        ]
    }],
    onsuccess: async function() {                    
       dadosPaineis.value = await dbPaineis.value.getAll()
    }
}))

const salvaPaineisCache = () => {
    return $vistra.Objetos.when(
        () => dbPaineis.value,
        async () => {
            await dbPaineis.value.clear()
            dadosPaineis.value.forEach(painel => {
                dbPaineis.value.add(JSON.parse(JSON.stringify(painel)))
            });
        })
}

const salvaPainelCache = (painel) => {
    dbPaineis.value.delete(painel.idUnico)
    dbPaineis.value.add(JSON.parse(JSON.stringify(painel)))
}

//#region C A R R E G A M E N T O   A P I
watch(idEmpresa, () => {
  carregaPaineis()
})
const carregaPaineis = async () => {
    carregandoPaineis.value = true;
    $http.get("/painel/GetPaineis/"+ $vistra.getDispositivo())
        .then(response => {
            for (let painel of response.data) {
                const painelCache = dadosPaineis.value.find(r => r.idUnico === painel.idUnico)
                // se ja tinha a imagem e ela não está velha, reutiliza pra não ter que buscar no back novamente
                if (painelCache && painelCache.imagem && new Date(painelCache.dataUltimaImagem) >= new Date(painel.dataUltimaImagem)) {
                    painel.imagem = painelCache.imagem
                    painel.imagemEscura = painelCache.imagemEscura
                }
            }
            
            if (!response.data?.msgErro) {                
                let paineis = response.data;
                paineis.forEach(painel => {
                    if (painel.traducao?.dicionario)
                        painel.descricao = traducao.traduz(painel.traducao.dicionario, 'painel-descricao', painel.descricao, true)
                })
                dadosPaineis.value = paineis
            }
         })
        .catch(e => {
            $vistra.showMessage(!e.data.msgErro ? e : e.data.msgErro, "error");
        })
        .then(() => {                    
            carregandoPaineis.value = false;                    
            salvaPaineisCache();
            carregaMiniaturas('#recentes#')
            carregaMiniaturas('#favoritos#')
        });
}

const carregaGrupos = async () => {
    return new Promise((resolve): void => {
        $http.get("/painel/GetGruposPaineis")
            .then(response => {
                dadosGrupos.value = dadosGrupos.value.concat(response.data.map(grupo => {
                    grupo.loading = true;
                    return grupo;
                }));
            })
            .catch(e => {
                $vistra.showMessage(!e.data.msgErro ? e : e.data.msgErro, "error");
            })
            .then(() => {                        
                //this.visLoading.removeMsg(this.$vistra.globalize("msg-carregando-grupos"))
                // console.log(dadosGrupos.value)
                resolve()
            });
    })
}

const carregaMiniaturas = (nomeGrupo) => {

    const chama = (vistra4) => {
        let ids = []
        switch(nomeGrupo) {
            case '#recentes#':
                ids = recentes.value.map(p => p.id)
                break;
            case '#favoritos#':
                ids = dadosPaineis.value.filter(p => p.favorito && p.vistra4 == vistra4 && !p.imagem && p.temImagem).map(p => p.id)
                break;
            default:
                ids = dadosPaineis.value.filter(p => p.nomeGrupo.toLowerCase() == nomeGrupo.toLowerCase() && p.vistra4 == vistra4 && !p.imagem && p.temImagem).map(p => p.id)
                break;
        }
        if (ids.length) {
            $http.get(`/painel/${vistra4 ? 'GetImagensPaineisV4' : 'GetImagensPaineis'}/?ids=${ids.join('&ids=')}`)
            .then(response => {
                if (response.data) {
                    response.data.forEach(imagens => {
                        let painel = dadosPaineis.value.find(p => p.id == imagens.id);
                        painel.imagem = `data:image/png;base64,${imagens.imagem.replace("data:image/png;base64,", "")}`;
                        painel.imagemEscura = imagens.imagemEscura ? `data:image/png;base64,${imagens.imagemEscura.replace("data:image/png;base64,", "")}` : painel.imagemEscura;
                        painel.dataUltimaImagem = imagens.dataUltimaImagem;
                        salvaPainelCache(painel)
                    })
                }
            })
        }
    }

    chama(false)
    chama(true)
}
//#endregion

//#region F I L T R O S

const filterByTerm = (dados): [] => {
  return dados.filter(p => {
        let encontrou = true;
        filtro.value.toLowerCase().match(/(["'])[^]*?\1|([^\s])+/g)?.forEach(element => {
            if ((!String(p.id).includes(element.toLowerCase())) &&
                (!p.nome.toLowerCase()
                    .replace(/[ÀÁÂÃÄÅ]/g,"A")
                    .replace(/[àáâãäå]/g,"a")
                    .replace(/[ÈÉÊË]/g,"E")
                    .replace(/[èéêë]/g,"e")
                    .replace(/[Ç]/g,"C")
                    .replace(/[ç]/g,"c")
                .includes(
                    element.toLowerCase()
                    .replace(/\"|\'/g, "")
                    .replace(/[ÀÁÂÃÄÅ]/g,"A")
                    .replace(/[àáâãäå]/g,"a")
                    .replace(/[ÈÉÊË]/g,"E")
                    .replace(/[èéêë]/g,"e")
                    .replace(/[Ç]/g,"C")
                    .replace(/[ç]/g,"c")))) {
                encontrou = false;
            }
        });
        return encontrou;
    })
}

const dsSearch = computed(() => {
    return dadosPaineis.value.map(p => ({label: p.nome, category: p.nomeGrupo}))
})

const filtered = (nomeGrupo) => {
    if(nomeGrupo.toLowerCase() == 'recentes') {
        return recentesFiltrados.value
    }

    return paineisFiltrados.value.filter((painel) => painel.nomeGrupo.toLowerCase() === nomeGrupo.toLowerCase());
};

const paineisFiltrados = computed(() => {
    if (filtro.value)
        return filterByTerm(dadosPaineis.value);
    return dadosPaineis.value
})

const recentes = computed(() => {
    return $vistra.ordenarPor(dadosPaineis.value.filter(p => p.ultimoAcesso), "ultimoAcesso").reverse().slice(0, 5)
})

const recentesFiltrados = computed(() => {
    if (filtro.value)
        return filterByTerm(recentes.value);
    return recentes.value
})

const favoritosFiltrados = computed(() => {
    if (filtro.value)
        return filterByTerm(dadosPaineis.value.filter(p => p.favorito));
    return dadosPaineis.value.filter(p => p.favorito)
})

let gruposFiltradosSideBar = computed(() => {
    let retorno = [];
    if(!filtro.value)
        retorno = [{id: 0, nome: $vistra.globalize('paineis-destaque'), icon: IconContainer, loading: true}]
    return retorno.concat(dadosGrupos.value.filter(g => { 
        return paineisFiltrados.value.find(p => p.nomeGrupo.toLowerCase() === g.nome.toLowerCase());
    }));
})

let gruposFiltrados = computed(() => {
    return dadosGrupos.value.filter(g => { 
        return paineisFiltrados.value.find(p => p.nomeGrupo.toLowerCase() === g.nome.toLowerCase());
    })
})

let timeoutSearch: ReturnType<typeof setTimeout> = 0;
const onValueChanged = (e) => {
    if((e.event.type == 'keyup' && e.component._changedValue && e.component._changedValue.length < 2) &&
        (e.event.type == 'input' && (!e.value || e.value.length < 2)) &&
        (e.event.type == 'dxclick' && !e.value))
        return
        
    if(timeoutSearch)
        clearTimeout(timeoutSearch)
    if(e.event.type == 'keyup') {
        filtro.value = e.component._changedValue
    } else if(e.event.type == 'dxclick') {
        filtro.value = e.value
    }
    else if(e.event.type == 'input') {
        timeoutSearch = setTimeout(() => filtro.value = e.value, 2000)
    }
}
//#endregion

//#region R E N D E R I Z A Ç Ã O
const goToPanelsMobile = () => {
  if(selected.value && $vistra.getDispositivo() == 'mobile'){
    //Se for selecionado um valor no mobile ele fecha e vai ate os paineis do grupo
    drawerMobile.value?.toggle()
  }
}

const goToCategory = (id, idx) => {
  selected.value = idx
  navegandoSideBar.value = true
  //e.preventDefault();
  if(id == 'Destaques0') {
    panelsContainer.value.scrollTo({
      top: 0
    })
    return;
  }
  panelsContainer.value.scrollTo({
    top: (document.getElementById(id).offsetTop - margemTop)
  })
  goToPanelsMobile();
}

const between = (x, min, max) => {
    return x >= min && x <= max;
}

let showPaineisOnScroll = () => {
    if(!categoriesRef.value || !panelsContainer.value)
        return
    
    let categoriaAtiva = false
    let categoryDestaque = document.getElementById('#Destaques0')
    categoriesRef.value?.forEach(element => {

        if( categoryDestaque && !navegandoSideBar.value && (panelsContainer.value.scrollTop + 230) >= categoryDestaque.offsetTop && (panelsContainer.value.scrollTop + 230) <= categoryDestaque.offsetTop + categoryDestaque.offsetHeight) {
            if(selected.value !== 0) {
                selected.value = 0;
                categoriaAtiva = true
            }
        }

        if(!categoriaAtiva && !navegandoSideBar.value && (panelsContainer.value.scrollTop + 230) >= element.offsetTop && (panelsContainer.value.scrollTop + 230) <= element.offsetTop + element.offsetHeight) {
            let idx = !filtro.value ? (+element.getAttribute('data-idx')) + 1 : (+element.getAttribute('data-idx'));

            if(selected.value !== idx) {
                selected.value = idx;
                categoriaAtiva = true
                let position = document.getElementById('lista-grupos').querySelectorAll('.sidebar-item')[idx].offsetTop - 200 //200 é uma margem pra não ficar bem no topo e pode ser mudada se achar necessário
                document.getElementById('lista-grupos').scrollTo({top: position})
            }
        }

        // carrega (se ainda não carregou) os paineis do grupo se este estiver na área visível da tela
        // ganha performance ao não carregar todos os paineis de todos os grupos ao mesmo tempo
        const areaVisivel = panelsContainer.value.scrollTop + window.innerHeight
        if (between(element.offsetTop, panelsContainer.value.scrollTop, areaVisivel) || between(element.offsetTop + element.offsetHeight, panelsContainer.value.scrollTop, areaVisivel)) {
            let grupo = dadosGrupos.value.find(grupo => (grupo.nome + grupo.id) == element.id)
            if(grupo?.loading) {
                //console.log("loading false grupo ", grupo.nome, "panelsContainer.value.scrollTop", panelsContainer.value.scrollTop, "element.offsetTop", element.offsetTop)
                setTimeout(() => { //tempo pra fazer a rolagem pro grupo e depois começar a pintar pra não ficar travado até carregar os painéis do grupo selecionado
                    grupo.loading = false
                }, 10)

                carregaMiniaturas(grupo.nome)
            }
        }
    })
    navegandoSideBar.value = false
}

watch([categoriesRef, paineisFiltrados, gruposFiltrados, selected],() => {
  nextTick(() => showPaineisOnScroll())
})
//#endregion

//#region A Ç Õ E S

// abre informações do painel
const onInfo = (props: CardProps) => {
  card.value = props;
  drawer.value?.toggle();
};

// marca/desmarca painel como favorito
const onFavorite = (idPainel) => {
  let painel = dadosPaineis.value.find(painel => painel.id == idPainel);
  if(!painel)
    return;

  painel.favorito = !painel.favorito
  if (painel.favorito)
      $http.post(`/Favorito/${painel.id}/${painel.vistra4}`)
          .catch(e => {
              $vistra.showMessage(!e.data.msgErro ? e : e.data.msgErro, "error");
          });
  else
      $http.delete(`/Favorito/${painel.id}/${painel.vistra4}`)
          .catch(e => {
              $vistra.showMessage(!e.data.msgErro ? e : e.data.msgErro, "error");
          });
}

// adiciona novo painel na lista após clonar
const addNovoPainel = (painel) => {
  dadosPaineis.value.push(painel);
}

// exclui painel
const excluir = (painel) => {
  $http.delete("/painel/DeletePainel/" + painel.id, {})
  .then(async response => {
      if (!!response.data.msgErro)
          $vistra.showMessage(response.data.msgErro, "warning",{displayTime: 10000});
      else {
          if (response.data.msg)
              $vistra.showMessage(response.data.msg);
          dadosPaineis.value.splice(dadosPaineis.value.indexOf(painel), 1);
          await salvaPaineisCache();
      }
  })
  .catch(e => {
      $vistra.showMessage(!e.data.msgErro ? e : e.data.msgErro, "error");
  })
}

// cria novo painel
const criar = async (e) => {
    $http.get("/Sistema/GetNovoId", {})
    .then(response => {
        $vistra.goTo("dev.painel/" + response.data);
    })
    .catch(e => {
        $vistra.showMessage(!e.data.msgErro ? e : e.data.msgErro, "error");
    });
}
//#endregion

//#region L I F E C Y C L E   H O O K S

onMounted(async () => {
  await carregaGrupos()
  await carregaPaineis()
  showPaineisOnScroll();
  panelsContainer.value?.addEventListener('scroll', (e) => {
    showPaineisOnScroll()
  })
})
//#endregion
const autocompleteRef = ref(null);
const isFocused = ref(false);
onMounted(() => {
  const input = autocompleteRef.value?.$el.querySelector('input');
  input?.addEventListener('focus', () => {
    isFocused.value = true;
  });

  input?.addEventListener('blur', () => {
    isFocused.value = false;
  });
});


</script>

<template>
   <Panel :show="show" id="menu-paineis">
    <div
      class="sidebar flex flex-col w-56 bg-white dark:bg-gray-900 border-r border-solid border-r-gray-200 dark:border-r-gray-700"
      :class="{'h-screen' : $vistra.getDispositivo() != 'mobile'}"
    >
      <button
        type="button"
        class="flex gap-2 pt-7 pb-8 pl-8 text-sm text-gray-900 dark:text-white items-center border-b border-solid border-b-gray-200 dark:border-b-gray-700 transition-all duration-300 ease-in-out hover:!text-primary-500"
        @click="$emit('onClose')"
      >
        <img alt="" src="/assets/svgs/icon-close.svg" />

        {{$vistra.globalize('menu-paineis')}}
      </button>

      <div v-if="$vistra.getDispositivo() != 'mobile'" class="sidebar-items flex p-8 gap-6 flex-col overflow-y-auto" id="lista-grupos">
        <a
          :key="nome + id"
          type="button"
          v-for="({ id, nome, icon, imagem }, index) in gruposFiltradosSideBar"
          class="sidebar-item flex gap-2 group items-center text-sm"
          @click="(e) => goToCategory(nome + id, index)"
          :class="
            selected === index
              ? 'is-selected text-primary-500'
              : 'text-gray-500 dark:text-gray-400'
          "
        >
          <span
            class="icon-group w-9 h-9 flex items-center justify-center rounded-full transition-all duration-300 ease-in-out"
            :class="
              selected === index
                ? 'bg-primary-500 !text-white group-hover:text-white'
                : 'bg-gray-50 dark:bg-gray-800 group-hover:text-primary-500'
            "
          >
            <img v-if="imagem" :src="`data:image/png;base64,${imagem}`" style="border-radius: 50%" />
            <component v-else :is="icon || IconProfiles" />
          </span>

          {{ nome }}
        </a>
      </div>
      <div v-else-if="$vistra.getDispositivo() == 'mobile'">
        <Drawer
          size="lg"
          ref="drawerMobile"
          :title="$vistra.globalize('menu-paineis')"
        >
          <a
            :key="nome + id"
            type="button"
            v-for="({ id, nome, icon, imagem }, index) in gruposFiltradosSideBar"
            class="sidebar-item flex pt-6 group items-center text-sm"
            @click="(e) => goToCategory(nome + id, index)"
            :class="
              selected === index
                ? 'is-selected text-primary-500'
                : 'text-gray-500 dark:text-gray-400'
            "
          >
            <span
              class="icon-group w-9 h-9 mr-2 flex items-center justify-center rounded-full transition-all duration-300 ease-in-out"
              :class="
                selected === index
                  ? 'bg-primary-500 !text-white group-hover:text-white'
                  : 'bg-gray-50 dark:bg-gray-800 group-hover:text-primary-500'
              "
            >
              <img v-if="imagem" :src="`data:image/png;base64,${imagem}`" style="border-radius: 50%;" />
              <component v-else :is="icon || IconProfiles" />
            </span>

            {{ nome }}
          </a>
        </Drawer>
      </div>
    </div>

    <div
      class="panel flex flex-col overflow-y-auto h-screen w-full"
      ref="panelsContainer"
    >
      <div class="search w-[calc(100%-222px)] flex justify-between pt-8 px-10 pb-3 bg-gray-50 dark:bg-gray-900">
        <DxAutocomplete
          :top="8"
          :value="search"
          :class="$vistra.getDispositivo() != 'mobile' ? 'max-w-[362px]' : ''"
          :width="'100%'"
          :buttons="icons(['find'])"
          value-expr="label"
          :placeholder="$vistra.globalize('pesquisar-painel')"
          :data-source="dsSearch"
          item-template="search"
          :search-timeout="400"
          :inputAttr="{id: 'inputPesquisa'}"
          :minSearchLength="3"
          :dropDownOptions="optionsDropdownSearch"
          :onValueChanged="onValueChanged"
          :onEnterKey="onValueChanged"
          valueChangeEvent="input change enter"
          ref="autocompleteRef"
        >
          <template #search="{ data }">
            <div :class="'flex text-sm items-center py-2 px-3' + $vistra.getDispositivo() == 'mobile' ? 'max-w-[250px]' : ''">
              <span class="max-w-full overflow-hidden text-ellipsis mr-2 text-gray-1000 dark:text-white">{{ data.label }}</span>
              <span class="py-1 px-2 text-xs ml-auto font-semibold border border-solid border-gray-300 dark:border-gray-500 rounded text-primary-500">{{ data.category }}</span>
            </div>
          </template>
        </DxAutocomplete>
        <DxButton
          v-if="$vistra.getDispositivo() == 'mobile'"
          id="btnNovoPainel"
          text="Grupos"
          style="width:100%"
          type="submit"
          :visible="!isFocused"
          @click="drawerMobile?.toggle()"
        />
      </div>

      
      <!--DESTAQUE (FIXO)-->
      <div id="#Destaques0" v-if="!filtro" :style="`margin-top: ${margemTop}px`">
        <!--RECENTES (FIXO)-->
        <div class="category"
        >
          <h2 class="title text-gray-900 dark:text-white px-10">
            {{$vistra.globalize('paineis-recentes')}}
          </h2>
    
          <div class="panel-cards mt-6 grid grid-cols-5 2xl:grid-cols-6 gap-5 px-10">
            <CardLoading
              v-if="carregandoPaineis && !filtered('recentes').length"
              v-for="(sk, indexF) in new Array(5)"
              :key="indexF"
              title=""
            />
            <Card
              v-else-if="filtered('recentes').length"
              v-for="(painel, index) in filtered('recentes')"
              :key="painel.id"
              :title="painel.nome"
              :painel="painel"
              @oninfo="onInfo({ title: painel.nome, preview: $vistra.estaNoModoEscuro() ? painel.imagemEscura : painel.imagem, favorite: painel.favorito, descricao: painel.descricao})"
              :preview="$vistra.estaNoModoEscuro() ? painel.imagemEscura : painel.imagem"
              :favorite="painel.favorito"
              @onfavorite="onFavorite"
              :usuario="usuario"
              @addNovoPainel="addNovoPainel"
              @excluir="excluir"
            /> 
            <h5 class="sem-dados" v-else>
              {{$vistra.globalize('nenhum-acesso')}}
            </h5>
          </div>
        </div>
        <!--RECENTES (FIXO)-->

        <!--FAVORITOS (FIXO)-->
        <div class="category"
        >
          <h2 class="title text-gray-900 dark:text-white px-10">
            {{$vistra.globalize('paineis-favoritos')}}
          </h2>
    
          <div class="panel-cards mt-6 grid grid-cols-5 2xl:grid-cols-6 gap-5 px-10">
            <CardLoading 
              v-if="carregandoPaineis && !favoritosFiltrados.length"
              v-for="(sk, indexF) in new Array(5)"
              :key="indexF"
              title=""
            ></CardLoading>
            <Card
              v-else-if="favoritosFiltrados.length"
              v-for="(painel, index) in favoritosFiltrados"
              :key="painel.id"
              :title="painel.nome"
              :painel="painel"
              @oninfo="onInfo({ title: painel.nome, preview: $vistra.estaNoModoEscuro() ? painel.imagemEscura : painel.imagem, favorite: painel.favorito, descricao: painel.descricao})"
              :preview="$vistra.estaNoModoEscuro() ? painel.imagemEscura : painel.imagem"
              :favorite="painel.favorito"
              @onfavorite="onFavorite"
              :usuario="usuario"
              @addNovoPainel="addNovoPainel"
              @excluir="excluir"
            />
            <h5 class="sem-dados" v-else>
              {{$vistra.globalize('nenhum-favorito')}}
            </h5>
          </div>
        </div>
        <!--RECENTES (FIXO)-->
      </div>
      
      <!--DESTAQUE (FIXO)-->


      <div class="category"
        :class="filtro ? 'pesquisando-paineis' : ''"
        v-for="({nome, id, loading}, idx) in gruposFiltrados"
        :key="nome + id"
        ref="categoriesRef"
        :id="`${nome + id}`"
        :data-idx="idx"
        >
        <h2 class="title text-gray-900 dark:text-white px-10">
          {{ nome }}
        </h2>
  
        <div class="panel-cards mt-6 grid grid-cols-5 2xl:grid-cols-6 gap-5 px-10">
          <CardLoading class="loading rounded dx-theme-text-color loading-pulsante" 
	          v-if="loading"
	          v-for="(painel, indexL) in filtered(nome)"
	          :key="indexL"
	          :title="painel.nome"
          ></CardLoading>

          <Card
            v-else
            v-for="(painel, index) in filtered(nome)"
            :key="painel.id"
            :title="painel.nome"
            :painel="painel"
            @oninfo="onInfo({ title: painel.nome, preview: $vistra.estaNoModoEscuro() ? painel.imagemEscura : painel.imagem, favorite: painel.favorito, descricao: painel.descricao})"
            :preview="$vistra.estaNoModoEscuro() ? painel.imagemEscura : painel.imagem"
            :favorite="painel.favorito"
            @onfavorite="onFavorite"
            :usuario="usuario"
            @addNovoPainel="addNovoPainel"
            @excluir="excluir"
          />
        </div>
      </div>
    </div>

    <Drawer ref="drawer"  :title="$vistra.globalize('btn-informacao')" size="lg">
      <div v-if="card" class="flex flex-col">
        <img v-if="card.preview" alt="" :src="card.preview" class="rounded-lg object-cover" style="max-height: 180px; max-width: 320px;"/>
        <i v-else alt="" class="fad fa-tree-map w-full rounded-lg object-cover" style="height: 222px; font-size: 222px; text-align: center;"></i>
        <div class="border-t border-solid border-t-gray-200 dark:border-t-gray-700 mt-6 pt-6" v-html="card.descricao" style="height: -webkit-fill-available;"></div>
      </div>
    </Drawer>
  </Panel>
</template>

<style lang="scss">
html {
  scroll-behavior: smooth;
}
.loading-pulsante {
    animation-duration: 1.5s;
    animation-iteration-count: infinite;
    animation-name: loading-color;
    @apply bg-white dark:bg-gray-900;
}

@keyframes loading-color {
    30% {
        filter: brightness(80%)
    }
    80% {
        filter: brightness(80%)
    }
}
#menu-paineis {
  @import '@/styles/base.scss';

  .search {
    position: fixed;
    z-index: 1501;
  }

  #btnNovoPainel {
    @apply bg-primary-500 px-3 hover:text-white #{!important}
  }
  .sem-dados {
    grid-column: 1 / 5;
  }
  .category {
    margin-bottom: 40px;
  }

  .category:last-child {
    margin-bottom: 100px;
  }

  .category.pesquisando-paineis:nth-of-type(2) {
    margin-top: 104px !important;
  }

  .icon-group {
    min-width: 36px;
  }

  .dx-texteditor {
    @apply shadow-none #{!important};
  }
  h2.title {
    @apply text-xl font-semibold #{!important};
  }
  h3 {
    @apply text-md font-medium;
  }

@media (max-width: 1490px) {
  .panel-cards {
    @apply grid-cols-4 #{!important};
  }
}

@media (max-width: 1250px) {
  .panel-cards {
    @apply grid-cols-3 #{!important};
  }
}

@media (max-width: 994px) {
  .sidebar {
    @apply w-full max-w-[11rem] #{!important};

    &-items {
      @apply p-5 #{!important};
    }
  }

  .panel {
    @apply w-[calc(100vw-11rem)];

    .search {
      z-index: 2;
      gap: 10px;
      @apply w-full max-w-full #{!important};

      .text {
        @apply overflow-hidden text-ellipsis mr-3 #{!important};
      }
    }

    &-cards {
      @apply grid-cols-2 #{!important};
    }
  }
}

@media (max-width: 700px) {
  .panel-cards {
    @apply gap-2 #{!important};
  }
}

@media (max-width: 620px) {
  .panel-cards {
    @apply grid-cols-1 #{!important};
  }
}

@media (max-width: 490px) {
  .sidebar {
    @apply max-w-full #{!important};

    & > button {
      @apply flex-row-reverse justify-between py-5 px-4 font-semibold text-xl #{!important};
    }

    &-items {
      @apply flex items-center flex-row overflow-x-auto py-6 px-4 #{!important};

      .sidebar-item {
        @apply py-2 px-4 rounded bg-gray-100 text-gray-400 dark:bg-gray-700 #{!important};

        &.is-selected {
          @apply bg-primary-100 text-primary-500 #{!important};

          span {
            @apply text-primary-500 #{!important};
          }
        }

        span {
          @apply text-gray-400 w-fit h-fit bg-transparent p-0 #{!important};
        }
      }
    }
  }

  .panel {
    .title {
      @apply text-lg mt-6 pt-6 border-t border-gray-200 dark:border-gray-800;
    }

    &-cards {
      @apply grid-cols-1 #{!important};
    }
  }
}

@media (max-width: 410px) {
  .panel {
    &-cards {
      @apply grid-cols-1 #{!important};
    }
  }
}
}

</style>
